import StoreScopeTag from "@/components/partner/StoreScopeTag";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Megaphone } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Iter-338 — vendor social pages + store category.
 * Contract (mirror pack): social_handles {instagram, facebook,
 * google_review_url} + top-level store_category on vendors/register and
 * PUT /vendors/me. Bad google_review_url → 422 with a human message,
 * surfaced verbatim next to the field.
 */

export const STORE_CATEGORIES = [
  ["hair_beauty", "Hair & beauty"],
  ["restaurant_cafe", "Restaurant & cafe"],
  ["retail", "Retail"],
  ["auto", "Auto"],
  ["health_fitness", "Health & fitness"],
  ["trades_services", "Trades & services"],
  ["other", "Other"],
];

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

export function emptySocial() {
  return {
    instagram: "", facebook: "", tiktok: "", google_review_url: "", store_category: "",
    // Iter-645 — exact tag customers paste, editable per platform.
    instagram_tag: "", facebook_tag: "", tiktok_tag: "",
  };
}

/** Builds the request fragment — only non-empty values are sent. */
export function socialPayload(v) {
  const handles = {};
  if (v.instagram.trim()) handles.instagram = v.instagram.trim();
  if (v.facebook.trim()) handles.facebook = v.facebook.trim();
  // Iter-412 §2 — server normalises @handle / bare / tiktok.com URL.
  if (v.tiktok.trim()) handles.tiktok = v.tiktok.trim();
  if (v.google_review_url.trim()) handles.google_review_url = v.google_review_url.trim();
  // Iter-645 — tags ride along whenever anything social is being saved.
  // Blank tags are sent too: the server then re-derives them from the
  // link/handle (so clearing a tag falls back to the derived one).
  const anyTag = ["instagram_tag", "facebook_tag", "tiktok_tag"].some((k) => (v[k] || "").trim());
  if (Object.keys(handles).length > 0 || anyTag) {
    handles.instagram_tag = (v.instagram_tag || "").trim();
    handles.facebook_tag = (v.facebook_tag || "").trim();
    handles.tiktok_tag = (v.tiktok_tag || "").trim();
  }
  const out = {};
  if (Object.keys(handles).length > 0) out.social_handles = handles;
  if (v.store_category) out.store_category = v.store_category;
  return out;
}

/** Controlled fields — used on the partner application form AND the
 *  dashboard edit card. All optional; never blocks submit. */
export function SocialCategoryFields({ value, onChange, urlError }) {
  const set = (k, val) => onChange({ ...value, [k]: val });
  return (
    <div className="space-y-3" data-testid="partner-social-fields">
      <div>
        <div className="text-xs font-bold text-slate-500 mb-1.5">Store category</div>
        <div className="flex flex-wrap gap-1.5">
          {STORE_CATEGORIES.map(([val, label]) => (
            <button
              key={val}
              type="button"
              data-testid={`partner-category-${val}`}
              onClick={() => set("store_category", value.store_category === val ? "" : val)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                value.store_category === val
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* Iter-645 — two columns per platform: page link + exact "Tag @…"
          customers paste. testIDs per mirror pack: engage-social-*-tag. */}
      <div className="grid grid-cols-2 gap-2">
        <input
          data-testid="partner-social-instagram"
          value={value.instagram}
          onChange={(e) => set("instagram", e.target.value)}
          placeholder="Instagram handle (e.g. @myshop)"
          className="pp-input"
        />
        <input
          data-testid="engage-social-instagram-tag"
          value={value.instagram_tag}
          onChange={(e) => set("instagram_tag", e.target.value)}
          placeholder="Tag @…"
          className="pp-input"
        />
        <input
          data-testid="partner-social-facebook"
          value={value.facebook}
          onChange={(e) => set("facebook", e.target.value)}
          placeholder="Facebook page (name or URL)"
          className="pp-input"
        />
        <input
          data-testid="engage-social-facebook-tag"
          value={value.facebook_tag}
          onChange={(e) => set("facebook_tag", e.target.value)}
          placeholder="Tag @…"
          className="pp-input"
        />
        <input
          data-testid="partner-social-tiktok"
          value={value.tiktok}
          onChange={(e) => set("tiktok", e.target.value)}
          placeholder="TikTok handle, e.g. @myshop"
          className="pp-input"
        />
        <input
          data-testid="engage-social-tiktok-tag"
          value={value.tiktok_tag}
          onChange={(e) => set("tiktok_tag", e.target.value)}
          placeholder="Tag @…"
          className="pp-input"
        />
      </div>
      <div>
        <input
          data-testid="partner-social-google"
          value={value.google_review_url}
          onChange={(e) => set("google_review_url", e.target.value)}
          placeholder="Google review link (https://g.page/r/…/review)"
          className="pp-input"
        />
        {urlError && (
          <p className="mt-1 text-xs text-red-600" data-testid="partner-social-google-error">{urlError}</p>
        )}
      </div>
      <p className="text-xs text-slate-400">
        Right column = the exact tag customers paste into their post (leave blank to use the handle from the link). Tagged posts are verified instantly.
      </p>
    </div>
  );
}

/** Dashboard edit card for existing vendors (PUT /vendors/me). */
export default function SocialCategoryCard() {
  const qc = useQueryClient();
  const meQ = useQuery({
    queryKey: ["vendor-me"],
    queryFn: async () => (await sharedApi.get("/classifieds/engagement/vendors/me", OPTS)).data,
  });
  const [form, setForm] = useState(emptySocial());
  const [urlError, setUrlError] = useState("");
  const vendor = meQ.data;

  useEffect(() => {
    if (!vendor) return;
    const h = vendor.social_handles || {};
    setForm({
      instagram: h.instagram || "",
      facebook: h.facebook || "",
      tiktok: h.tiktok || "",
      google_review_url: h.google_review_url || "",
      store_category: vendor.store_category || "",
      // Iter-645 — pre-filled with the saved/derived tag, editable.
      instagram_tag: h.instagram_tag || "",
      facebook_tag: h.facebook_tag || "",
      tiktok_tag: h.tiktok_tag || "",
    });
  }, [vendor]);

  const save = useMutation({
    mutationFn: async () =>
      (await sharedApi.put("/classifieds/engagement/vendors/me", socialPayload(form), OPTS)).data,
    onSuccess: () => {
      setUrlError("");
      toast.success("Social pages saved.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : null;
      if (err?.response?.status === 422 && msg) setUrlError(msg);
      else toast.error(msg || "Couldn't save your social pages");
    },
  });

  if (!vendor) return null;
  const h = vendor.social_handles || {};
  const allEmpty = !h.instagram && !h.facebook && !h.tiktok && !h.google_review_url;

  return (
    <div className="mt-6 rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="partner-social-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        <Megaphone className="w-4 h-4" /> Social pages &amp; category
        <StoreScopeTag />
      </div>
      {allEmpty && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="partner-social-nudge">
          No social pages set — customers can&apos;t tag you yet.
        </div>
      )}
      <div className="mt-4">
        <SocialCategoryFields value={form} onChange={setForm} urlError={urlError} />
      </div>
      <button
        type="button"
        data-testid="partner-social-save"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-6 py-2.5 transition-colors disabled:opacity-60"
      >
        {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Save
      </button>
    </div>
  );
}
